import { useCallback, useMemo } from "react";
import { useContactStore, useUserStore, useSyncStore } from "@/stores/StoreProvider";
import type { Contact, ContactRequest } from "@/types/crm";
import { toast } from "sonner";
import { toastUndo } from "@/components/ui/undo-toast";

export function useContacts() {
  const contactStore = useContactStore();
  const syncStore = useSyncStore();
  return { data: contactStore.all as unknown as Contact[], isLoading: !syncStore.bootstrapComplete };
}

export function useCompanyContacts() {
  const { data, ...rest } = useContacts();
  return { ...rest, data: data?.filter((c) => c.type === 0) };
}

export function useContact(id: string) {
  const contactStore = useContactStore();
  const userStore = useUserStore();
  const syncStore = useSyncStore();
  const raw = id ? contactStore.getById(id) : undefined;
  const data = useMemo(() => raw ? {
    ...raw,
    ownerName: raw.ownerId ? userStore.getById(raw.ownerId)?.displayName : undefined,
    companyName: raw.companyId ? contactStore.getById(raw.companyId)?.name : undefined,
  } as Contact : undefined, [raw]);
  // Show loading only while bootstrap is in progress AND the item isn't in the store yet
  return { data, isLoading: !data && !syncStore.bootstrapComplete };
}

export function useCompanyEmployees(companyId: string) {
  const contactStore = useContactStore();
  const syncStore = useSyncStore();
  const data = companyId ? contactStore.getByCompanyId(companyId) : [];
  return { data, isLoading: !syncStore.bootstrapComplete };
}

export function useCreateContact() {
  const contactStore = useContactStore();
  const create = async (data: ContactRequest) => {
    try {
      contactStore.create(data);
      toast.success("Contact created");
    } catch (error) {
      toast.error("Failed to create contact");
      throw error;
    }
  };
  return { mutateAsync: create, mutate: (data: ContactRequest) => { create(data); }, isPending: false };
}

export function useUpdateContact() {
  const contactStore = useContactStore();
  const update = async ({ id, data }: { id: string; data: ContactRequest }) => {
    try {
      contactStore.update(id, data);
      toast.success("Contact updated");
    } catch (error) {
      toast.error("Failed to update contact");
      throw error;
    }
  };
  return { mutateAsync: update, mutate: (args: { id: string; data: ContactRequest }) => { update(args); }, isPending: false };
}

export function useDeleteContact() {
  const contactStore = useContactStore();
  const syncStore = useSyncStore();
  // Stable identities: ContactsTable's column memo depends on this object.
  // A new object each render recreates every cell component and loses state
  // (for example an open row-action confirmation dialog).
  const mutateAsync = useCallback(
    async ({ id, name }: { id: string; name?: string }) => {
      contactStore.delete(id);
      toastUndo(
        name ? `"${name}" deleted` : "Contact deleted",
        () => {
          syncStore.cancelPendingMutation('contact', 'delete', id);
          contactStore.restore(id);
        },
      );
    },
    [contactStore, syncStore]
  );
  const mutate = useCallback(
    (args: { id: string; name?: string }) => {
      void mutateAsync(args);
    },
    [mutateAsync]
  );

  return useMemo(
    () => ({ mutateAsync, mutate, isPending: false }),
    [mutateAsync, mutate]
  );
}
